/** Atletas salvos neste navegador (item 2.7). Até a conta online (item 6.x), o elenco vive no localStorage. */
import { EXAMPLE_KITS, normalizeName, profileProblems, type AthleteProfile } from "@besenliga/shared";

export type KitId = keyof typeof EXAMPLE_KITS;
export const KIT_IDS = Object.keys(EXAMPLE_KITS) as KitId[];

export interface SavedAthlete extends AthleteProfile {
  id: string;
  kit: KitId;
  savedAt: string;
}

export const ROSTER_KEY = "besenliga.atletas.v1";
export const ROSTER_MAX = 12;

type Store = Pick<Storage, "getItem" | "setItem">;

function isSavedAthlete(value: unknown): value is SavedAthlete {
  if (typeof value !== "object" || value === null) return false;
  const a = value as Partial<SavedAthlete>;
  return (
    typeof a.id === "string" &&
    typeof a.savedAt === "string" &&
    typeof a.kit === "string" &&
    KIT_IDS.includes(a.kit) &&
    profileProblems(a as AthleteProfile).length === 0
  );
}

/** Lê o elenco salvo. Entradas corrompidas ou de versões antigas são ignoradas. */
export function loadRoster(store: Store | null): SavedAthlete[] {
  try {
    const raw = store?.getItem(ROSTER_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isSavedAthlete).slice(0, ROSTER_MAX) : [];
  } catch {
    return [];
  }
}

export type SaveResult =
  | { ok: true; roster: SavedAthlete[]; athlete: SavedAthlete }
  | { ok: false; reason: "invalido" | "cheio" | "armazenamento" };

/** Salva (ou atualiza, pelo id) um atleta e devolve o elenco novo. */
export function saveAthlete(
  store: Store | null,
  roster: readonly SavedAthlete[],
  draft: Omit<SavedAthlete, "savedAt">,
  now: Date,
): SaveResult {
  const athlete: SavedAthlete = { ...draft, name: normalizeName(draft.name), savedAt: now.toISOString() };
  if (!isSavedAthlete(athlete)) return { ok: false, reason: "invalido" };
  const exists = roster.some((a) => a.id === athlete.id);
  if (!exists && roster.length >= ROSTER_MAX) return { ok: false, reason: "cheio" };
  const next = exists ? roster.map((a) => (a.id === athlete.id ? athlete : a)) : [...roster, athlete];
  if (!writeRoster(store, next)) return { ok: false, reason: "armazenamento" };
  return { ok: true, roster: next, athlete };
}

export function deleteAthlete(
  store: Store | null,
  roster: readonly SavedAthlete[],
  id: string,
): SavedAthlete[] | null {
  const next = roster.filter((a) => a.id !== id);
  return writeRoster(store, next) ? next : null;
}

function writeRoster(store: Store | null, roster: readonly SavedAthlete[]): boolean {
  if (!store) return false;
  try {
    store.setItem(ROSTER_KEY, JSON.stringify(roster));
    return true;
  } catch {
    return false;
  }
}

/** localStorage, quando o navegador deixa usar (aba anônima e bloqueios podem impedir). */
export function browserStore(): Store | null {
  try {
    const s = window.localStorage;
    const probe = "besenliga.teste";
    s.setItem(probe, "1");
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}
